import { Trans } from '@lingui/react/macro';

import ChevronDown from '@/assets/svg/chevron-down.svg?react';
import { PlainButton, type PlainButtonProps } from '@/components/buttons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/DropdownMenu';
import { cn } from '@/lib/utils';

type LogicButtonValue = 'OR' | 'AND';

export type LogicButtonProps = Omit<PlainButtonProps, 'size' | 'color'> & {
  value: LogicButtonValue;
  isReadOnly?: boolean;
  onChange?: (value: LogicButtonValue) => void;
};

const LogicButton = ({
  className,
  value = 'OR',
  isReadOnly = false,
  disabled = false,
  onChange,
  ...rest
}: LogicButtonProps) => {
  if (isReadOnly) {
    return (
      <PlainButton
        className={cn('h-6', 'pointer-events-none cursor-default', className)}
        color={value === 'OR' ? 'aqua' : 'green'}
        tabIndex={-1}
        disabled={disabled}
        {...rest}
      >
        {value === 'OR' ? <Trans>OR</Trans> : <Trans>AND</Trans>}
      </PlainButton>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={disabled}
        render={(triggerProps) => (
          <PlainButton
            {...triggerProps}
            className={cn('h-6 pe-0.5', triggerProps.className, className)}
            color={value === 'OR' ? 'aqua' : 'green'}
            disabled={disabled}
            {...rest}
          >
            <span className="flex items-center gap-1">
              {value === 'OR' ? <Trans>OR</Trans> : <Trans>AND</Trans>}
              <ChevronDown />
            </span>
          </PlainButton>
        )}
      />
      <DropdownMenuContent>
        <DropdownMenuGroup>
          <DropdownMenuLabel>
            <Trans>Condition Type</Trans>
          </DropdownMenuLabel>
          <DropdownMenuRadioGroup
            value={value}
            onValueChange={(nextValue: LogicButtonValue) => onChange?.(nextValue)}
          >
            <DropdownMenuRadioItem closeOnClick value="OR">
              <Trans>OR</Trans>
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem closeOnClick value="AND">
              <Trans>AND</Trans>
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LogicButton;
